import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Calendar,
  Filter,
  Check,
  X,
  Clock,
  ChevronDown,
  MessageSquare
} from 'lucide-react';
import { api } from '../../lib/api';
import { useUIStore } from '../../store/ui';
import { formatDate, formatPrice, getTrackColorClasses } from '../../lib/utils';

interface Booking {
  id: string;
  teamId: string;
  teamName: string;
  teamSlug: string;
  bookedByName: string;
  bookedByEmail: string;
  trackTypeId: string;
  trackName: string;
  trackColor: string;
  timeSlotId: string;
  slotName: string;
  bookingDate: string;
  startTime: string;
  endTime: string;
  status: string;
  requestNotes?: string;
  adminNotes?: string;
  pricePerSlot: number;
  createdAt: string;
}

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'requested', label: 'Pending Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function AdminBookings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useUIStore();

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');

  // Action modal state
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, [statusFilter]);

  async function fetchBookings() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      params.append('sort', 'createdAt:desc');

      const res = await api.get(`/api/admin/bookings?${params.toString()}`);
      if (res.bookings) {
        setBookings(res.bookings);
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleAction() {
    if (!selectedBooking || !actionType) return;

    setProcessing(true);
    try {
      await api.patch(`/api/admin/bookings/${selectedBooking.id}/status`, {
        status: actionType === 'approve' ? 'approved' : 'rejected',
        adminNotes,
      });

      setBookings(
        bookings.map((b) =>
          b.id === selectedBooking.id
            ? {
                ...b,
                status: actionType === 'approve' ? 'approved' : 'rejected',
                adminNotes,
              }
            : b
        )
      );

      addToast(
        `Booking ${actionType === 'approve' ? 'approved' : 'rejected'} successfully`,
        'success'
      );
      closeModal();
    } catch (error) {
      addToast('Failed to update booking status', 'error');
    } finally {
      setProcessing(false);
    }
  }

  function openActionModal(booking: Booking, action: 'approve' | 'reject') {
    setSelectedBooking(booking);
    setActionType(action);
    setAdminNotes(booking.adminNotes || '');
  }

  function closeModal() {
    setSelectedBooking(null);
    setActionType(null);
    setAdminNotes('');
  }

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      requested: 'bg-yellow-900/50 text-yellow-400',
      approved: 'bg-green-900/50 text-green-400',
      rejected: 'bg-red-900/50 text-red-400',
      cancelled: 'bg-gray-700 text-gray-400',
    };
    return styles[status] || 'bg-gray-700 text-gray-400';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Bookings</h1>
          <p className="text-gray-400 mt-1">
            Manage and review track booking requests.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
        <div className="flex items-center space-x-4">
          <Filter size={18} className="text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setSearchParams(e.target.value ? { status: e.target.value } : {});
            }}
            className="bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-red-500 focus:border-red-500"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Bookings List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" />
        </div>
      ) : bookings.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-12 text-center border border-gray-700">
          <Calendar className="mx-auto text-gray-600 mb-4" size={48} />
          <h2 className="text-xl font-semibold text-white mb-2">
            No bookings found
          </h2>
          <p className="text-gray-400">
            {statusFilter
              ? 'No bookings match your current filter.'
              : 'No booking requests yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((booking) => {
            const trackColors = getTrackColorClasses(booking.trackColor);

            return (
              <div
                key={booking.id}
                className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden"
              >
                <div className={`h-1 ${trackColors.bg}`} />
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-semibold text-white">
                          {booking.teamName}
                        </h3>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(
                            booking.status
                          )}`}
                        >
                          {booking.status}
                        </span>
                      </div>

                      <div className="grid md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Track:</span>{' '}
                          <span className="text-gray-300">{booking.trackName}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Date:</span>{' '}
                          <span className="text-gray-300">
                            {formatDate(booking.bookingDate)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Time:</span>{' '}
                          <span className="text-gray-300">
                            {booking.startTime} - {booking.endTime}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Booked by:</span>{' '}
                          <span className="text-gray-300">{booking.bookedByName}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Email:</span>{' '}
                          <span className="text-gray-300">{booking.bookedByEmail}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Price:</span>{' '}
                          <span className="text-white font-medium">
                            {formatPrice(booking.pricePerSlot)}
                          </span>
                        </div>
                      </div>

                      {booking.requestNotes && (
                        <div className="mt-3 p-3 bg-gray-700/50 rounded-lg">
                          <div className="flex items-start space-x-2">
                            <MessageSquare size={16} className="text-gray-400 mt-0.5" />
                            <div>
                              <span className="text-sm text-gray-400">Request notes:</span>
                              <p className="text-sm text-gray-300">{booking.requestNotes}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {booking.adminNotes && (
                        <div className="mt-2 p-3 bg-blue-900/20 rounded-lg border border-blue-800/50">
                          <span className="text-sm text-blue-400">Admin response:</span>
                          <p className="text-sm text-blue-300">{booking.adminNotes}</p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    {booking.status === 'requested' && (
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => openActionModal(booking, 'approve')}
                          className="btn btn-sm bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Check size={16} className="mr-1" />
                          Approve
                        </button>
                        <button
                          onClick={() => openActionModal(booking, 'reject')}
                          className="btn btn-sm bg-red-600 hover:bg-red-700 text-white"
                        >
                          <X size={16} className="mr-1" />
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Action Modal */}
      {selectedBooking && actionType && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl shadow-xl max-w-md w-full border border-gray-700">
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">
                {actionType === 'approve' ? 'Approve Booking' : 'Reject Booking'}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              {/* Booking Summary */}
              <div className="bg-gray-700/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Team</span>
                  <span className="text-white">{selectedBooking.teamName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Track</span>
                  <span className="text-white">{selectedBooking.trackName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Date</span>
                  <span className="text-white">
                    {formatDate(selectedBooking.bookingDate)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Time</span>
                  <span className="text-white">
                    {selectedBooking.startTime} - {selectedBooking.endTime}
                  </span>
                </div>
              </div>

              {/* Admin Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {actionType === 'reject' ? 'Rejection Reason' : 'Notes'}{' '}
                  {actionType === 'reject' && <span className="text-red-400">*</span>}
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                  required={actionType === 'reject'}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-red-500 focus:border-red-500"
                  placeholder={
                    actionType === 'reject'
                      ? 'Please provide a reason for rejection...'
                      : 'Optional notes for the team...'
                  }
                />
              </div>

              {actionType === 'approve' && (
                <div className="bg-green-900/20 border border-green-800/50 rounded-lg p-4">
                  <p className="text-sm text-green-400">
                    An approval email will be sent to {selectedBooking.bookedByEmail}{' '}
                    with payment instructions.
                  </p>
                </div>
              )}

              {actionType === 'reject' && (
                <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-4">
                  <p className="text-sm text-red-400">
                    A rejection email will be sent to {selectedBooking.bookedByEmail}{' '}
                    with the reason provided.
                  </p>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn flex-1 bg-gray-700 text-white hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAction}
                  disabled={processing || (actionType === 'reject' && !adminNotes)}
                  className={`btn flex-1 text-white ${
                    actionType === 'approve'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {processing
                    ? 'Processing...'
                    : actionType === 'approve'
                    ? 'Approve Booking'
                    : 'Reject Booking'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
